import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthApiError } from '@supabase/supabase-js';
import { useAuth } from '@/lib/auth';
import { AuthShell } from '@/pages/auth/AuthShell';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { supabase } from '@/lib/supabase';

/**
 * Patient portal login page.
 * Authenticates users and verifies they have patient records before allowing access.
 */
export function PortalLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const from = (location.state as { from?: Location } | null)?.from?.pathname ?? 
    new URLSearchParams(location.search).get('from') ?? 
    '/portal/dashboard';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    try {
      // Step 1: Authenticate with Supabase
      await signIn(email, password);

      // Step 2: Verify patient has portal access
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        throw new Error('Failed to verify session');
      }

      const { data: patients, error: patientError } = await supabase
        .from('patients')
        .select('id, first_name, last_name')
        .eq('portal_user_id', authUser.id)
        .eq('status', 'active')
        .limit(1);

      if (patientError) {
        console.error('Portal login: Error checking patient record', patientError);
        throw new Error('Failed to verify patient record');
      }

      if (!patients || patients.length === 0) {
        setError('No patient portal access found. Please contact your clinic.');
        await supabase.auth.signOut();
        setSubmitting(false);
        return;
      }

      // Success - navigate to portal
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Portal login error:', err);
      if (err instanceof AuthApiError) {
        if (err.code === 'invalid_credentials' || err.status === 400) {
          setError(t('auth.errors.invalid_credentials', 'Invalid email or password'));
        } else if (err.status === 429) {
          setError(t('auth.errors.too_many_requests', 'Too many attempts. Please try again later.'));
        } else {
          setError(err.message || t('auth.errors.unknown', 'An error occurred. Please try again.'));
        }
      } else {
        setError(t('auth.errors.unknown', 'An error occurred. Please try again.'));
      }
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async (e: FormEvent) => {
    e.preventDefault();
    setResetStatus('loading');
    setError(null);

    try {
      // Verify patient has portal access before sending reset email
      const { data: patients, error: patientError } = await supabase
        .from('patients')
        .select('id')
        .eq('email', resetEmail.toLowerCase())
        .not('portal_user_id', 'is', null)
        .limit(1);

      if (patientError) {
        throw patientError;
      }

      if (!patients || patients.length === 0) {
        setError('No patient record found for this email.');
        setResetStatus('error');
        return;
      }

      // Send password reset email
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/portal/reset-password`,
      });

      if (resetError) throw resetError;

      setResetStatus('success');
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to send reset email. Please try again.');
      setResetStatus('error');
    }
  };

  if (showResetForm) {
    return (
      <AuthShell
        title="Reset Password"
        subtitle="Enter your email to receive a password reset link"
        footer={
          <button
            onClick={() => {
              setShowResetForm(false);
              setResetStatus('idle');
              setError(null);
            }}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
          >
            Back to login
          </button>
        }
      >
        {error && <ErrorAlert message={error} />}
        {resetStatus === 'success' && (
          <div className="mb-4 rounded-md bg-green-50 p-4 dark:bg-green-900/50">
            <p className="text-sm font-medium text-green-800 dark:text-green-200">
              Password reset email sent! Check your inbox.
            </p>
          </div>
        )}
        <form onSubmit={handlePasswordReset} className="space-y-6">
          <Input
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            disabled={resetStatus === 'success'}
          />
          <Button
            type="submit"
            size="full"
            disabled={resetStatus === 'loading' || resetStatus === 'success'}
          >
            {resetStatus === 'loading' ? 'Sending...' : resetStatus === 'success' ? 'Sent!' : 'Send Reset Link'}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Patient Portal"
      subtitle="Sign in to access your patient portal"
      footer={
        <>
          Need help?{' '}
          <Link to="/" className="font-medium text-indigo-600 hover:text-indigo-500">
            Contact your clinic
          </Link>
        </>
      }
    >
      {error && <ErrorAlert message={error} />}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => setShowResetForm(true)}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
          >
            Forgot password?
          </button>
        </div>

        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>
    </AuthShell>
  );
}
